import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { Box, Typography, useTheme } from "@material-ui/core";
import { useFieldValue } from "formik";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import useStyles from "../../containers/data/forms/forms-styles";
import { EMPTY_VALUE_PLACEHOLDER } from "../../models/common/data";
import { Options } from "../../models/common/options";
import { AttributeSize } from "../../models/flexible/attribute-size";
import { ControlState } from "../../models/flexible/control-state";
import { DataSubType, DataType } from "../../models/flexible/data-type";
import { FlexControlProps, FlexFieldController } from "../../models/flexible/flex-field-controller";
import { StatusHistorical } from "../../models/flexible/status-historical";
import { FlexContainerType, Maybe } from "../../models/generated/graphql";
import formatCurrency from "../../utils/currency-format";
import { isEmptyValue } from "../../utils/is-empty-value";
import { localeDate } from "../../utils/locale-date";
import { neo4jDateToDate } from "../../utils/neo4j-date-to-date";
import { TrFun } from "../../utils/types";
import FlexChat from "../flex-widgets/FlexChat";
import FlexTable, { mapAttributeToTable } from "../flex-widgets/FlexTable";
import { FormDateRange } from "../FormDateRange";
import FormRadioGroup from "../FormRadioGroup";
import LACheckBox from "../LACheckBox";
import LAComplete from "../LAComplete";
import LyfeFormField from "../LyfeFormField";

interface IProps {
	controller: FlexFieldController;
	path: string;
	initialRowIndex?: number;
}

const selectOptionsComponent = (
	controller: FlexFieldController,
	path: string,
	controlProps: FlexControlProps,
	state: ControlState,
	translate: TrFun
) => {
	const { controlType, ...otherControlProps } = controlProps;

	if (controlType === "radio") {
		return (
			<FormRadioGroup
				name={path}
				fullWidth
				label={translate(controller.schema.attribute.shortLabelLocalizationKey)}
				disabled={state === "disabled"}
				required={controller.schema.attribute.validation !== StatusHistorical.Optional}
				{...(otherControlProps as any)}
			/>
		);
	}

	if (controlType === "combo") {
		return (
			<LyfeFormField
				name={path}
				fullWidth
				label={translate(controller.schema.attribute.shortLabelLocalizationKey)}
				select
				disabled={state === "disabled"}
				required={controller.schema.attribute.validation !== StatusHistorical.Optional}
				{...(otherControlProps as any)}
			/>
		);
	}

	if (controlType === "typeahead") {
		return (
			<LAComplete
				name={path}
				fullWidth
				disabled={state === "disabled"}
				title={translate(controller.schema.attribute.shortLabelLocalizationKey)}
				required={controller.schema.attribute.validation !== StatusHistorical.Optional}
				isChildInFormik={!controller.parent.context.isEditable}
				{...(otherControlProps as any)}
			/>
		);
	}

	if (controlType === "textArea") {
		return (
			<LyfeFormField
				name={path}
				fullWidth
				multiline
				label={translate(controller.schema.attribute.shortLabelLocalizationKey)}
				select
				disabled={state === "disabled"}
				required={controller.schema.attribute.validation !== StatusHistorical.Optional}
				{...(otherControlProps as any)}
			/>
		);
	}

	throw new Error(`Unknown control type: ${controlType}`);
};

const getWidgetComponent = (
	widgetType: DataSubType,
	controller: FlexFieldController,
	path: string,
	controlProps: FlexControlProps,
	state: ControlState
) => {
	switch (widgetType) {
		case DataSubType.Table:
			const tableType = mapAttributeToTable[controller.schema.attribute.identifier];
			return <FlexTable flexTableType={tableType} />;

		case DataSubType.Chat:
			return <FlexChat controller={controller} path={path} controlProps={controlProps} state={state} />;

		default:
			return <></>;
	}
};

const fieldFactories: {
	[K in DataType]: (
		controller: FlexFieldController,
		path: string,
		controlProps: FlexControlProps,
		state: ControlState,
		translate: TrFun
	) => JSX.Element;
} = {
	[DataType.Text]: (
		controller: FlexFieldController,
		path: string,
		controlProps: FlexControlProps,
		state: ControlState,
		translate: TrFun
	) =>
		controlProps?.options ? (
			selectOptionsComponent(controller, path, controlProps, state, translate)
		) : (
			<LyfeFormField
				id={controller.schema.attribute.shortLabelLocalizationKey}
				name={path}
				multiline={controlProps?.controlType === "textArea"}
				fullWidth
				label={translate(controller.schema.attribute.shortLabelLocalizationKey)}
				disabled={state === "disabled"}
				required={controller.schema.attribute.validation !== StatusHistorical.Optional}
				{...(controlProps as any)}
			/>
		),
	[DataType.Number]: (
		controller: FlexFieldController,
		path: string,
		controlProps: FlexControlProps,
		state: ControlState,
		translate: TrFun
	) =>
		controlProps?.options ? (
			selectOptionsComponent(controller, path, controlProps, state, translate)
		) : (
			<LyfeFormField
				id={controller.schema.attribute.shortLabelLocalizationKey}
				name={path}
				type="number"
				fullWidth
				label={translate(controller.schema.attribute.shortLabelLocalizationKey)}
				disabled={state === "disabled"}
				required={controller.schema.attribute.validation !== StatusHistorical.Optional}
				{...(controlProps as any)}
			/>
		),
	[DataType.Date]: (
		controller: FlexFieldController,
		path: string,
		controlProps: FlexControlProps,
		state: ControlState,
		translate: TrFun
	) =>
		controlProps?.isTimeframe ? (
			<FormDateRange
				id={controller.schema.attribute.shortLabelLocalizationKey}
				name={path}
				label={translate(controller.schema.attribute.shortLabelLocalizationKey)}
				disabled={state === "disabled"}
				required={controller.schema.attribute.validation !== StatusHistorical.Optional}
				labelStyles={{ color: "black", fontSize: "12px" }}
				{...(controlProps as any)}
			/>
		) : (
			<LyfeFormField
				id={controller.schema.attribute.shortLabelLocalizationKey}
				name={path}
				type="date"
				fullWidth
				label={translate(controller.schema.attribute.shortLabelLocalizationKey)}
				disabled={state === "disabled"}
				required={controller.schema.attribute.validation !== StatusHistorical.Optional}
				{...(controlProps as any)}
			/>
		),
	[DataType.Boolean]: (
		controller: FlexFieldController,
		path: string,
		controlProps: FlexControlProps,
		state: ControlState,
		translate: TrFun
	) =>
		controlProps?.options || controlProps?.controlType === "radio" ? (
			selectOptionsComponent(controller, path, controlProps, state, translate)
		) : (
			<LACheckBox
				id={controller.schema.attribute.shortLabelLocalizationKey}
				label={translate(controller.schema.attribute.shortLabelLocalizationKey)}
				attributeClasses={controller.schema.attribute.attributeClasses}
				name={path}
				labelplacement="end"
				labelsize="medium"
				{...(controlProps as any)}
			/>
		),
	[DataType.Widget]: (
		controller: FlexFieldController,
		path: string,
		controlProps: FlexControlProps,
		state: ControlState,
		translate: TrFun
	) =>
		controlProps?.options
			? selectOptionsComponent(controller, path, controlProps, state, translate)
			: getWidgetComponent(controller.schema.attribute.dataSubType!, controller, path, controlProps, state),
} as any;

export function getColumnClassName(size: AttributeSize, styles: ReturnType<typeof useStyles>) {
	switch (size) {
		case AttributeSize.Short:
			return styles.colShort;
		case AttributeSize.Medium:
			return styles.colMedium;
		case AttributeSize.Long:
			return styles.colLong;
		case AttributeSize.Extralong:
			return styles.colExtraLong;
		default:
			throw new Error(`Attribute width ${size} unknown`);
	}
}

function formatTimeframe(value?: [Maybe<Date>, Maybe<Date>]) {
	if (!value || (!value[0] && !value[1])) {
		return EMPTY_VALUE_PLACEHOLDER;
	}

	return `${value[0] ? localeDate(neo4jDateToDate(value[0])!) : EMPTY_VALUE_PLACEHOLDER} - ${
		value[1] ? localeDate(neo4jDateToDate(value[1]))! : EMPTY_VALUE_PLACEHOLDER
	}`;
}

export function formatFieldValue(
	value: any,
	dataType: DataType,
	controlProps: FlexControlProps,
	translate: TrFun,
	decimalPrecision?: number | null
) {
	if (isEmptyValue(value)) {
		return value;
	}

	switch (dataType) {
		case DataType.Boolean:
			return translate(value === true || value === "true" ? "yes" : "no");

		case DataType.Date:
			if (controlProps?.isTranslated && controlProps?.translator) {
				return translate(controlProps.translator(value));
			}

			return controlProps?.isTimeframe ? formatTimeframe(value) : localeDate(neo4jDateToDate(value)!);

		case DataType.Number:
			if (decimalPrecision) {
				return formatCurrency(value, decimalPrecision, decimalPrecision);
			}
			return `${value}`;

		case DataType.Text:
			if (controlProps?.isTranslated && controlProps?.keyGenerator) {
				return translate(controlProps.keyGenerator(value));
			}

			if (controlProps?.isTranslated && controlProps?.translator) {
				return translate(controlProps.translator(value));
			}

			return value;

		default:
			return value;
	}
}

function getFormattedListLabel(value: any, controlProps: FlexControlProps): string {
	if (isEmptyValue(value)) {
		return value;
	}

	if (Array.isArray(value)) {
		return value.map((element) => getFormattedListLabel(element, controlProps)).join(", ");
	}

	if (controlProps.controlType === "combo") {
		const { options } = controlProps;
		return (
			((options as Array<Options>) || []).find((option) => option.value === value)?.label ||
			(controlProps.displayValueIfOptionNotFound && value) ||
			EMPTY_VALUE_PLACEHOLDER
		);
	}

	const { options } = controlProps;
	const property: string = controlProps.property || "id";
	const optionLabel: string = controlProps.optionLabel || "label";
	const matchedOption =
		((options as Array<any>) || []).find(
			(option) => option[property] === (controlProps.selectPropertyValue ? value : value[property])
		) || value;
	if (!matchedOption) {
		return value;
	}

	if (controlProps.getOptionLabel) {
		return controlProps.getOptionLabel(matchedOption);
	}

	return typeof matchedOption === "string" ? matchedOption : matchedOption[optionLabel];
}

function ReadOnlyField(props: IProps & { state: ControlState }) {
	const { t } = useTranslation();
	const { palette } = useTheme();

	const controlProps = props.controller.useControlProps(props.path);
	const [value] = useFieldValue(props.path);
	const labelLocalizationKey = props.controller.schema.attribute.shortLabelLocalizationKey;
	const formattedValue =
		controlProps?.controlType === "combo" || controlProps?.controlType === "typeahead"
			? getFormattedListLabel(value, controlProps)
			: formatFieldValue(
					value,
					props.controller.schema.attribute.dataType,
					controlProps,
					t,
					props.controller.schema.attribute.decimalPrecision
			  );
	const percentSymbol = [AttributeIdentifiers.SuggestedDiscountRate, AttributeIdentifiers.DiscountRate].includes(
		props.controller.schema.attribute.identifier as AttributeIdentifiers
	)
		? "%"
		: "";
	const currencySymbol = ` ${controlProps?.currency ?? ""}`;

	return (
		<Box>
			<Typography variant="caption" color="textSecondary">
				{t(labelLocalizationKey)}
			</Typography>
			<Typography style={{ color: palette.tableText.main, wordBreak: "break-word" }}>
				{isEmptyValue(formattedValue)
					? controlProps?.emptyValuePlaceholder || EMPTY_VALUE_PLACEHOLDER
					: formattedValue + percentSymbol + currencySymbol}
			</Typography>
		</Box>
	);
}

function EditableField(props: IProps & { state: ControlState }) {
	const { t } = useTranslation();
	const controlProps = props.controller.useControlProps(props.path);

	return fieldFactories[props.controller.schema.attribute.dataType](props.controller, props.path, controlProps, props.state, t);
}

function getIsImpossibleToEdit(props: IProps) {
	if (!props.controller.parent.context.impossibleToEditAttributes?.length) {
		return false;
	}

	const relevantAttributeDescriptors = props.controller.parent.context.impossibleToEditAttributes.filter(
		({ identifier }) => identifier === props.controller.schema.attribute.identifier
	);

	if (relevantAttributeDescriptors?.length && isEmptyValue(relevantAttributeDescriptors[0].row)) {
		return true;
	}

	return relevantAttributeDescriptors.some(({ row }) => row === props.initialRowIndex);
}

export function FieldWrapper(props: IProps) {
	const state = props.controller.useControlState(props.path);
	const width = props.controller.getWidth?.();
	const styles = useStyles();
	const colClass =
		props.controller.parent.context.entityType.containerType === FlexContainerType.Record
			? styles.colLong
			: getColumnClassName(width || props.controller.schema.attribute.expectedWidth || AttributeSize.Medium, styles);

	if (state === "hidden") {
		return null;
	}

	if (state === "empty") {
		return (
			<Fragment>
				{props.controller.renderBefore?.(props.path)}
				<Box className={colClass} pr={5} paddingY={2.5} pl={1.5}>
					{props.controller.renderBeforeContent?.(props.path)}
					<></>
					{props.controller.renderAfterContent?.(props.path)}
				</Box>
				{props.controller.renderAfter?.(props.path)}
			</Fragment>
		);
	}

	if (props.controller.customRenderContent) {
		return props.controller.customRenderContent(props.path);
	}

	const editable =
		props.controller.parent.context.isEditable &&
		state !== "disabled" &&
		!props.controller.schema.attribute.isDisabled &&
		!getIsImpossibleToEdit(props);
	const isDifferentPadding =
		props.controller.schema.attribute.shortLabelLocalizationKey === AttributeIdentifiers.SuggestedDiscountRate && editable;

	return (
		<Fragment>
			{props.controller.renderBefore?.(props.path)}
			<Box
				className={colClass}
				pr={5}
				paddingY={2.5}
				pl={editable ? undefined : 1.5}
				style={{
					paddingTop: isDifferentPadding ? 28 : "",
				}}
			>
				{props.controller.renderBeforeContent?.(props.path)}
				{editable ? <EditableField state={state} {...props} /> : <ReadOnlyField state={state} {...props} />}
				{props.controller.renderAfterContent?.(props.path)}
			</Box>
			{props.controller.renderAfter?.(props.path)}
		</Fragment>
	);
}
